// @givmo/board — the committees a seat sits on, as a director reads them (CBG-UX10, D-CBG-40).
//
// Design C's menu holds Committees "only when the seat sits on one", and names a page for it; no
// frame draws it, so it is the officer's Committees page cut to what the director's door serves:
// the officer's table of committees, one row each, with the person's own role on it ("Chair" or
// "Member", the committee member dialog's words) and its next meeting, the soonest one ahead that
// stands, by its day in the meeting's own zone. No chair column (the door doesn't serve who else
// sits on a committee), no charter, no "Acts for the board", no "Add a committee".
//
// Two reads the director pages already make: `/me`'s seat, whose committees the registry records,
// and the meetings ahead under the Meetings page's key, whose committee meetings carry their
// committee (the read of the ones ahead, so none is lost past a hundred: review-291's F2). A failed
// read says so, as on Meetings, so "—" only ever means no meeting ahead (review-291's F3). A
// committee's own page is the officer's alone; a director's committee meetings, consents and minutes
// are on the board's Meetings, Written consents and Minute book.

import type { BoardSeatCommitteeOut } from '@givmo/api-client';
import { Card, DataTable, EmptyState, Icon, type DataTableColumn } from '@givmo/design-system';
import { Network } from 'lucide-react';
import { directorBoardIdentity, useDirectorBoardAddress } from '../../director/director-board';
import { DirectorReadFailed } from '../../director/access-refusal';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { useMinuteClock } from '../../lib/minute-clock';
import { meetingDay, meetingHasStarted } from '../meetings/meetings-decode';
import { AllYourBoards } from './all-your-boards';
import { DirectorBoardContent } from './director-board-content';
import { useDirectorMeetings } from './director-meetings-page';
import { directorPagesWords as w } from './director-pages-words';
import { DirectorShell } from './director-shell';
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';

export function DirectorCommitteesPage() {
  const { orgRef, board, notYours, scope, me, settled } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const committees = board?.committees ?? [];
  const meetingsQuery = useDirectorMeetings(orgRef, mayFire && committees.length > 0, 'ahead');
  const now = useMinuteClock(null);
  const meetings = meetingsQuery.data ?? [];

  /** The committee's soonest meeting ahead that stands, its day; "—" with none. */
  const nextMeeting = (committeeId: string): string => {
    const next = meetings.find(
      (meeting) =>
        meeting.committee_id === committeeId &&
        meeting.status === 'scheduled' &&
        !meetingHasStarted(meeting.starts_at, now),
    );
    return next === undefined ? '—' : meetingDay(next);
  };

  const columns: DataTableColumn<BoardSeatCommitteeOut>[] = [
    {
      id: 'name',
      header: w.columnCommittee,
      stacked: 'heading',
      cell: (committee) => (
        <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
          {committee.name}
        </span>
      ),
    },
    {
      id: 'role',
      header: w.columnYourRole,
      stackedLabel: w.columnYourRole,
      cell: (committee) => (committee.role === 'chair' ? w.roleChair : w.roleMember),
      width: '220px',
    },
    {
      id: 'next',
      header: cm.columnNextMeeting,
      stackedLabel: cm.columnNextMeeting,
      cell: (committee) => (
        <span
          className="tabular-nums"
          data-testid={`director-committee-next-${committee.committee_id}`}
        >
          {meetingsQuery.isSuccess ? nextMeeting(committee.committee_id) : null}
        </span>
      ),
      width: '160px',
    },
  ];

  return (
    <DirectorShell
      title={w.committeesTitle}
      description={w.committeesLine}
      identity={directorBoardIdentity(board)}
      actions={
        <AllYourBoards
          me={me}
          settled={settled}
          committeeOnly={scope.committeeOnly}
          testId="director-committees-back"
        />
      }
      testId="page-director-committees"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        <DirectorReadRefusal reads={[meetingsQuery]} testId="director-committees-read-step-up" />
        {meetingsQuery.isError && !isSigningRefusal(meetingsQuery.error) ? (
          <DirectorReadFailed
            read={meetingsQuery}
            line="We couldn’t load your meetings just now."
            testId="director-committees-error"
            retryTestId="director-committees-retry"
          />
        ) : null}
        {committees.length > 0 ? (
          <Card className="overflow-hidden" testId="director-committees-table">
            <DataTable
              columns={columns}
              rows={committees}
              rowKey={(committee) => committee.committee_id}
              rowTestId={(committee) => `director-committee-${committee.committee_id}`}
              stackAt="sm"
            />
          </Card>
        ) : settled ? (
          <EmptyState
            testId="director-committees-empty"
            icon={<Icon icon={Network} size={20} aria-hidden />}
            title={w.noCommitteesTitle}
            description={w.noCommitteesLine}
          />
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}
